/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

// StatCard.tsx
import React from "react";

type StatCardVariant = "default" | "success" | "danger" | "info" | "warning";

interface StatCardProps {
	label: string;
	sublabel: string;
	value: number | string;
	icon: string; // material-symbols icon name
	variant?: StatCardVariant;
	active?: boolean;
	clickable?: boolean;
	onClick?: () => void;
}
const variantStyles: Record<
	StatCardVariant,
	{
		border: string;
		ring: string;
		iconBg: string;
		iconHoverBg: string;
		labelColor: string;
		statColor: string;
		hoverBorder: string;
	}
> = {
	default: {
		border: "border-[#FF7043]",
		ring: "ring-4 ring-orange-50",
		iconBg: "bg-slate-100",
		iconHoverBg: "group-hover:bg-slate-200",
		labelColor: "text-slate-300",
		statColor: "text-[#1E293B]",
		hoverBorder: "hover:border-slate-300",
	},
	success: {
		border: "border-emerald-500",
		ring: "ring-4 ring-emerald-50",
		iconBg: "bg-emerald-50",
		iconHoverBg: "group-hover:bg-emerald-100",
		labelColor: "text-emerald-300",
		statColor: "text-emerald-600",
		hoverBorder: "hover:border-emerald-200",
	},
	danger: {
		border: "border-red-500",
		ring: "ring-4 ring-red-50",
		iconBg: "bg-red-50",
		iconHoverBg: "group-hover:bg-red-100",
		labelColor: "text-red-300",
		statColor: "text-red-600",
		hoverBorder: "hover:border-red-200",
	},
	info: {
		border: "border-blue-500",
		ring: "ring-4 ring-blue-50",
		iconBg: "bg-blue-50",
		iconHoverBg: "group-hover:bg-blue-100",
		labelColor: "text-blue-300",
		statColor: "text-blue-600",
		hoverBorder: "hover:border-blue-200",
	},
	warning: {
		border: "border-orange-500",
		ring: "ring-4 ring-orange-50",
		iconBg: "bg-orange-50",
		iconHoverBg: "group-hover:bg-orange-100",
		labelColor: "text-orange-300",
		statColor: "text-orange-600",
		hoverBorder: "hover:border-orange-200",
	},
};

const PACleaningStatCard: React.FC<StatCardProps> = ({
	label,
	sublabel,
	value,
	icon,
	variant = "default",
	active = false,
	clickable = false,
	onClick,
}) => {
	const styles = variantStyles[variant];

	return (
		<div
			onClick={onClick}
			className={`
        group bg-white p-4 rounded-2xl border transition-all duration-300
        ${clickable ? "cursor-pointer" : ""}
        ${
				active
					? `${styles.border} ${styles.ring} shadow-md`
					: `border-slate-200 ${styles.hoverBorder} shadow-sm`
			}
      `}
		>
			<div className="flex justify-between items-start mb-1">
				<div
					className={`w-6 h-6 ${styles.iconBg} rounded-xl flex items-center justify-center ${styles.iconHoverBg} transition-colors`}
				>
					<span
						className="material-symbols-outlined"
						style={{ color: `var(--icon-color)` }}
					>
						{icon}
					</span>
				</div>
				<span
					className={`text-[10px] font-black ${styles.labelColor} uppercase tracking-widest`}
				>
					{label}
				</span>
			</div>
			<p className={`text-3xl font-black ${styles.statColor}`}>{value}</p>
			<p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">
				{sublabel}
			</p>
		</div>
	);
};

export default PACleaningStatCard;
